import { JobDecorator } from 'decorators'
import { http, queryString } from 'utils'
import { omit, concat, map, set, findIndex } from 'lodash'

// Job mutation imports.
import {
  SET_JOBS, SET_JOBS_PAGINATION, ADD_JOBS, SET_JOB_ATTRIBUTE, RESET_JOB,
  SET_JOB, REMOVE_JOB, ADD_LOAD, ADD_JOB, RESET_JOBS
} from 'vx/mutations'

const initialJobState = () => {
  return {
    coordinator_id: null,
    user_id: null,
    start_at: null,
    end_at: null,
    quantity: null,
    commodity: null,
    commodity_id: null,
    order_reference: null,
    pickup_reference: null,
    delivery_reference: null,
    location_from: null,
    location_from_id: null,
    location_to: null,
    location_to_id: null,
    rate: null,
    request_price: false,
    notes: null,
    notify_job_status: false,
    notify_load_deliveries: false,
    notify_job_completed: false,
    notify_children: false,
    public: false,
    public_quantity: 0
  }
}

// Job state.
const state = {
  jobs: [],
  pagination: {},
  job: initialJobState()
}

// Job state getters.
const getters = {
  /**
   * Jobs decorated via the JobDecorator.
   */
  jobsDecorated: state => map(state.jobs, job => (new JobDecorator(job))),

  /**
   * The job payload excludes the relationship objects.
   */
  jobPayload: state => omit(state.job, ['location_from', 'location_to', 'commodity']),

  /**
   * Decorated JobDecorator
   */
  jobDecorated: state => (new JobDecorator(state.job)),

  /**
   * Determine if we have a job.
   */
  haveJob: state => state.job.id !== null && state.job.id !== undefined
}

// Job actions.
const actions = {
  /**
   * Get all jobs.
   */
  getJobs ({ commit }, payload) {
    return http.get(`jobs?${queryString(payload)}`).then(response => {
      commit(SET_JOBS, response.data.data)
      commit(SET_JOBS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the jobs for the next page.
   */
  getJobsForNextPage ({ commit, state }, payload) {
    if (state.pagination.from === state.pagination.to || state.pagination.to < state.pagination.per_page) {
      return Promise.reject('There are no more jobs to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`jobs?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_JOBS, response.data.data)
      commit(SET_JOBS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Retrieve a job by ID.
   */
  getJob ({ commit }, id) {
    return http.get(`jobs/${id}`).then(response => {
      commit(SET_JOB, response.data)

      return response
    })
  },

  /**
   * Store a new job.
   */
  storeJob (store, payload) {
    return http.post(`jobs`, payload)
  },

  /**
   * Update an existing job.
   */
  updateJob ({ commit }, { id, payload }) {
    return http.put(`jobs/${id}`, payload)
  },

  /**
   * Update an existing jobs planned load order.
   */
  updatePlannedLoadOrder (store, { id, payload }) {
    return http.put(`jobs/${id}/reorder-loads`, payload)
  },

  /**
   * Accept a job.
   */
  acceptJob ({ commit }, id) {
    return http.post(`jobs/${id}/accept`)
  },

  /**
   * Decline a job.
   */
  declineJob ({ commit }, id) {
    return http.post(`jobs/${id}/decline`)
  },

  /**
   * Finalise a job.
   */
  finaliseJob ({ commit }, id) {
    return http.post(`jobs/${id}/finalise`).then(response => {
      commit(SET_JOB, response.data)

      return response
    })
  },

  /**
   * Unfinalise a job.
   */
  unfinaliseJob ({ commit }, id) {
    return http.post(`jobs/${id}/unfinalise`).then(response => {
      commit(SET_JOB, response.data)

      return response
    })
  },

  /**
   * Complete a job.
   */
  completeJob ({ commit }, id) {
    return http.post(`jobs/${id}/complete`).then(response => {
      commit(SET_JOB, response.data)

      return response
    })
  },

  /**
   * Cancel a job.
   */
  cancelJob ({ commit }, id) {
    return http.post(`jobs/${id}/cancel`).then(response => {
      commit(SET_JOB, response.data)

      return response
    })
  },

  /**
   * Update a jobs notifications.
   */
  updateNotifications (store, { id, payload }) {
    return http.put(`jobs/${id}/notifications`, payload)
  },

  /**
   * Retrieve public jobs
   */
  getJobBoardItems ({ commit }, payload) {
    return http.get(`job-board?${queryString(payload)}`).then(response => {
      commit(SET_JOBS, response.data.data)
      commit(SET_JOBS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the public jobs for the next page.
   */
  getJobBoardItemsForNextPage ({ commit, state }, payload) {
    if (state.pagination.from === state.pagination.to || state.pagination.to < state.pagination.per_page) {
      return Promise.reject('There are no more jobs to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`job-board?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_JOBS, response.data.data)
      commit(SET_JOBS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the distance between a job's two locations
   */
  getJobDistance ({ commit }, id) {
    return http.get(`job/${id}/distance`).then(response => {
      return response
    })
  }
}

// Job state mutations.
const mutations = {
  /**
   * Set the jobs.
   */
  [SET_JOBS] (state, jobs) {
    state.jobs = jobs
  },

  /**
   * Add more jobs to the jobs state.
   */
  [ADD_JOBS] (state, jobs) {
    state.jobs = concat(state.jobs, jobs)
  },

  /**
   * Set the job pagination.
   */
  [SET_JOBS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Set a job attribute to a given value.
   */
  [SET_JOB_ATTRIBUTE] (state, { attribute, value }) {
    set(state.job, attribute, value)
  },

  /**
   * Reset job back to initial job state.
   */
  [RESET_JOB] (state) {
    state.job = initialJobState()
  },

  /**
   * Reset jobs back to empty array.
   */
  [RESET_JOBS] (state) {
    state.jobs = []
  },

  /**
   * Set the state job.
   */
  [SET_JOB] (state, job) {
    state.job = job
  },

  /**
   * Add a job or replace an existing.
   */
  [ADD_JOB] (state, job) {
    let existingJob = findIndex(state.jobs, existing => existing.id === job.id)

    if (existingJob === -1) {
      state.jobs.push(job)
    } else {
      state.jobs.splice(existingJob, 1, job)
    }
  },

  /**
   * Add a load to the jobs loads.
   */
  [ADD_LOAD] (state, load) {
    let existingLoad = findIndex(state.job.loads, existing => existing.id === load.id)

    if (existingLoad === -1) {
      state.job.loads.push(load)
    } else {
      state.job.loads.splice(existingLoad, 1, load)
    }
  },

  /**
   * Remove a job from the array of jobs.
   */
  [REMOVE_JOB] (state, job) {
    state.jobs.splice(findIndex(state.jobs, existing => existing.id === job.id), 1)
  }
}

// Job modules.
import plannedLoads from './job/planned-loads'
import history from './job/history'
import load from './job/load'

const modules = {
  plannedLoads,
  history,
  load
}

// Export our modules, state, getters, actions, and mutations.
export default {
  namespaced: true, modules, state, getters, actions, mutations
}



// WEBPACK FOOTER //
// ./src/vuex/modules/job.js